import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, DatePicker, Form, Input, message, Result, Select, Typography } from 'antd'
import { Link } from 'react-router-dom'
import { getPersonalKycStatus, submitPersonalKyc } from '../../services/kyc'
import { getMerchant } from '../../services/merchant'
import { FileUploadItem, FileUrls, KycStatusData } from './KycShared'
import type { Merchant } from '../../types'
import MerchantAgreementConsent from '../../components/legal/MerchantAgreementConsent'
import { ApiError } from '../../services/api'
import type { AgreementSelection } from '../../services/merchantAgreement'

const { Title } = Typography

const NATIONALITY_OPTIONS = [
  { value: 'CN', label: '中國' },
  { value: 'HK', label: '香港' },
  { value: 'SG', label: '新加坡' },
  { value: 'US', label: '美國' },
  { value: 'RU', label: '俄羅斯' },
]

interface PersonalKycFormValues {
  nationality: string
  fullName: string
  gender: 'MALE' | 'FEMALE' | 'OTHER'
  birthDate: { format: (pattern: string) => string }
  documentType: 'PASSPORT' | 'ID_CARD' | 'DRIVER_LICENSE' | 'OTHER'
  documentNumber: string
  address: string
}

export default function KycSubmit() {
  const [personalKycStatus, setPersonalKycStatus] = useState<KycStatusData | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [fileUrls, setFileUrls] = useState<FileUrls>({})
  const [merchant, setMerchant] = useState<Merchant | null>(null)
  const [agreementOpen, setAgreementOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<PersonalKycFormValues>()
  const [agreementRefreshKey, setAgreementRefreshKey] = useState(0)
  const uploadGeneration = useRef(0)
  const [personalForm] = Form.useForm()
  const selectedDocumentType = Form.useWatch('documentType', personalForm)
  const isPassport = selectedDocumentType === 'PASSPORT'
  const currentUploadGeneration = uploadGeneration.current

  useEffect(() => {
    Promise.all([getPersonalKycStatus(), getMerchant()])
      .then(([statusRes, merchantRes]) => {
        setPersonalKycStatus(statusRes.data)
        setMerchant(merchantRes.data)
      })
      .finally(() => setLoading(false))
  }, [])

  const handleFileUploaded = (key: keyof FileUrls, url: string) => {
    if (currentUploadGeneration !== uploadGeneration.current) return
    setFileUrls((prev) => ({ ...prev, [key]: url || undefined }))
  }

  const requiredPersonalFilesUploaded =
    !!fileUrls.personalDocumentFrontUrl &&
    !!fileUrls.personalDocumentBackUrl &&
    !!fileUrls.personalHeldIdPhotoUrl
  const merchantProfileComplete = !!merchant?.website?.trim() && !!merchant?.description?.trim()

  const handlePersonalSubmit = (values: PersonalKycFormValues) => {
    if (!merchantProfileComplete) {
      message.error('提交個人 KYC 前，請先在設定中補全網站和商戶描述')
      return
    }
    if (!requiredPersonalFilesUploaded) {
      message.error('請完成所有證件照片上傳')
      return
    }

    setPendingValues(values)
    setAgreementOpen(true)
  }

  const handleAgreementConfirm = async (agreementSelection: AgreementSelection) => {
    if (!pendingValues) return

    setSubmitting(true)
    try {
      await submitPersonalKyc({
        nationality: pendingValues.nationality,
        fullName: pendingValues.fullName,
        gender: pendingValues.gender,
        birthDate: pendingValues.birthDate.format('YYYY-MM-DD'),
        documentType: pendingValues.documentType,
        documentNumber: pendingValues.documentNumber,
        address: pendingValues.address,
        documentFrontUrl: fileUrls.personalDocumentFrontUrl!,
        documentBackUrl: fileUrls.personalDocumentBackUrl!,
        heldIdPhotoUrl: fileUrls.personalHeldIdPhotoUrl!,
        ...agreementSelection,
      })
      message.success('個人 KYC 已提交，請等待審核')
      setAgreementOpen(false)
      setPendingValues(undefined)
      getPersonalKycStatus().then((res) => setPersonalKycStatus(res.data))
      personalForm.resetFields()
    } catch (error: unknown) {
      if (error instanceof ApiError && [40911, 40912, 40913, 40914].includes(error.code ?? 0)) {
        setAgreementRefreshKey((key) => key + 1)
        message.warning('協議或簽署者資料已更新，請重新閱讀並確認')
        return
      }
      message.error(error instanceof Error ? error.message : '提交失敗')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <Card loading style={{ maxWidth: 720 }} />
  }

  if (personalKycStatus?.status === 'APPROVED') {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>個人 KYC</Title>
        <Result status="success" title="個人 KYC 已通過" />
      </div>
    )
  }

  if (personalKycStatus?.status === 'PENDING') {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>個人 KYC</Title>
        <Result status="info" title="個人 KYC 審核中" />
      </div>
    )
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>個人 KYC</Title>

      {!merchantProfileComplete && (
        <Alert
          type="warning"
          message="提交個人 KYC 前需補全商戶資訊"
          description="請先在賬戶設定中填寫網站和商戶描述。"
          action={<Link to="/dashboard/settings"><Button size="small">前往設定</Button></Link>}
          style={{ maxWidth: 720, marginBottom: 24 }}
          showIcon
        />
      )}

      <Card title="個人 KYC" style={{ maxWidth: 720 }}>
        {personalKycStatus?.status === 'REJECTED' && (
          <Alert
            type="error"
            message="個人 KYC 被拒絕"
            description={personalKycStatus.rejectReason}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Form form={personalForm} layout="vertical" onFinish={handlePersonalSubmit}>
          <Form.Item name="nationality" label="國籍" rules={[{ required: true, message: '請選擇國籍' }]}>
            <Select placeholder="請選擇國籍" options={NATIONALITY_OPTIONS} />
          </Form.Item>
          <Form.Item name="fullName" label="姓名" rules={[{ required: true }]}>
            <Input placeholder="Full Name" />
          </Form.Item>
          <Form.Item name="gender" label="性別" rules={[{ required: true }]}>
            <Select options={[
              { value: 'MALE', label: '男' },
              { value: 'FEMALE', label: '女' },
              { value: 'OTHER', label: '其他' },
            ]} />
          </Form.Item>
          <Form.Item name="birthDate" label="出生日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="documentType" label="證件類型" rules={[{ required: true }]}>
            <Select onChange={() => {
              uploadGeneration.current += 1
              setFileUrls({})
            }} options={[
              { value: 'PASSPORT', label: '護照' },
              { value: 'ID_CARD', label: '身份證' },
              { value: 'DRIVER_LICENSE', label: '駕照' },
              { value: 'OTHER', label: '其他' },
            ]} />
          </Form.Item>
          <Form.Item name="documentNumber" label="證件號碼" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="地址" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <FileUploadItem
            key={`${selectedDocumentType}-front`}
            label={isPassport ? '護照資料頁' : '證件正面'}
            fieldKey="personalDocumentFrontUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalDocumentFrontUrl}
          />
          <FileUploadItem
            key={`${selectedDocumentType}-back`}
            label={isPassport ? '護照封面或補充頁' : '證件背面'}
            fieldKey="personalDocumentBackUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalDocumentBackUrl}
          />
          <FileUploadItem
            key={`${selectedDocumentType}-held`}
            label={isPassport ? '本人手持護照照片' : '本人手持證件照片'}
            fieldKey="personalHeldIdPhotoUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalHeldIdPhotoUrl}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!requiredPersonalFilesUploaded || !merchantProfileComplete}
            >
              提交
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <MerchantAgreementConsent
        open={agreementOpen}
        candidateLegalName={pendingValues?.fullName ?? ''}
        authorizedUserName={pendingValues?.fullName ?? ''}
        submissionType="PERSONAL"
        merchantAccountId={merchant?.id ?? ''}
        registeredEmail={merchant?.email ?? ''}
        refreshKey={agreementRefreshKey}
        confirming={submitting}
        onCancel={() => {
          if (submitting) return
          setAgreementOpen(false)
          setPendingValues(undefined)
        }}
        onConfirm={handleAgreementConfirm}
      />
    </div>
  )
}
